import { SETTINGS_SECTION } from './constants'
import { buildAccessSubnavSlugs } from './settingsConfig'

/**
 * Context bag passed to `getValidTabSlugs(ctx)`. Sections opt in to flag-aware filtering by
 * defining the predicate in {@link SETTINGS_MEMORY_ROUTE_TAB_CONFIG}; sections without a predicate
 * accept every key of `paths`.
 *
 * @typedef {object} SettingsMemoryRouteValidTabsCtx
 * @property {boolean} [joinAnOrgForAccessNav] — `obi_home_new_join_an_org` variant for `adminAccess`
 * @property {boolean} [bookingVerificationEnabled] — `trust_and_safety_email_verification_feature` for personal `security`
 * @property {boolean} [reactiveBlockEnabled] — `trust_and_safety_reactive_block` for personal `security`
 */

/**
 * Maps `settings_tab=<slug>` to MemoryRouter initial paths for admin sections that use tabbed routes.
 * Add entries when a memory-routed settings page uses {@link PageHeaderWithTabs}; slug is stable for URLs.
 *
 * @typedef {object} SettingsMemoryRouteTabEntry
 * @property {string} defaultTab — slug used when `settings_tab` is missing or invalid
 * @property {Record<string, string>} paths — slug → full path (must match admin {@link Route} paths)
 * @property {(ctx: SettingsMemoryRouteValidTabsCtx) => string[]} [getValidTabSlugs] — runtime filter; defaults to `Object.keys(paths)`
 */

/** @type {Partial<Record<string, SettingsMemoryRouteTabEntry>>} */
export const SETTINGS_MEMORY_ROUTE_TAB_CONFIG = {
  [SETTINGS_SECTION.adminAccess]: {
    defaultTab: 'domain_control',
    paths: {
      domain_control: '/app/admin/access/domain_control',
      user_access: '/app/admin/access/user_access',
      sso: '/app/admin/access/sso',
      user_provisioning: '/app/admin/access/user_provisioning',
      activity_log: '/app/admin/access/activity_log',
    },
    /**
     * `domain_control` and `user_access` are mutually exclusive depending on the
     * `obi_home_new_join_an_org` variant — only one is in the UI at a time.
     */
    getValidTabSlugs: (ctx) => buildAccessSubnavSlugs(Boolean(ctx?.joinAnOrgForAccessNav)),
  },
  [SETTINGS_SECTION.adminSecurity]: {
    defaultTab: 'data_deletion',
    paths: {
      data_deletion: '/app/admin/security/data_deletion',
      activity_log: '/app/admin/security/activity_log',
      booking: '/app/admin/security/booking',
      visibility: '/app/admin/security/visibility',
    },
  },
  [SETTINGS_SECTION.security]: {
    defaultTab: 'booking',
    paths: {
      booking: '/app/personal/security/booking',
      blocked_sources: '/app/personal/security/blocked_sources',
      cookies: '/app/personal/security/cookies',
    },
    /** `booking` requires booking verification; `blocked_sources` requires reactive block; `cookies` is always available. */
    getValidTabSlugs: (ctx) => {
      const slugs = []
      if (ctx?.bookingVerificationEnabled) slugs.push('booking')
      if (ctx?.reactiveBlockEnabled) slugs.push('blocked_sources')
      slugs.push('cookies')
      return slugs
    },
  },
  [SETTINGS_SECTION.profile]: {
    defaultTab: 'general',
    paths: {
      general: '/app/personal/profile/general',
      login: '/app/personal/profile/login',
    },
  },
  [SETTINGS_SECTION.calendar]: {
    defaultTab: 'calendar',
    paths: {
      calendar: '/app/availability/calendar_settings',
      availability: '/app/availability/schedules',
      advanced: '/app/availability/advanced_settings',
    },
  },
}

/**
 * Currently-valid `settings_tab=` slugs for a section, after applying any flag-aware filter.
 * Returns `null` for sections without a memory-route tab config (callers typically interpret that
 * as "no tabs to validate").
 *
 * @param {string} section
 * @param {SettingsMemoryRouteValidTabsCtx} [ctx]
 * @returns {string[] | null}
 */
export function getValidTabsForSection(section, ctx) {
  const cfg = SETTINGS_MEMORY_ROUTE_TAB_CONFIG[section]
  if (!cfg) {
    return null
  }
  if (cfg.getValidTabSlugs) {
    return cfg.getValidTabSlugs(ctx ?? {})
  }
  return Object.keys(cfg.paths)
}

/**
 * @param {object} args
 * @param {string} args.section — {@link SETTINGS_SECTION} value
 * @param {string | null} [args.settingsTab] — slug from `settings_tab` query param
 * @param {string} args.basePath — memory shell base (e.g. `/app/admin/access`)
 * @param {string | undefined} [args.registryInitialPath] — default from section_content_registry (e.g. permissions)
 * @returns {string} — path for {@link MemoryRouter} `initialEntries`
 */
export function resolveMemoryRouteInitialPath({
  section,
  settingsTab,
  basePath,
  registryInitialPath,
}) {
  const cfg = SETTINGS_MEMORY_ROUTE_TAB_CONFIG[section]
  if (!cfg) {
    return registryInitialPath ?? basePath
  }
  if (settingsTab && cfg.paths[settingsTab]) {
    return cfg.paths[settingsTab]
  }
  if (registryInitialPath) {
    return registryInitialPath
  }
  return cfg.paths[cfg.defaultTab] ?? basePath
}

/**
 * @param {string} section — {@link SETTINGS_SECTION} value
 * @param {string} pathname — memory router pathname
 * @returns {string | null} — tab slug for `settings_tab`, or null if unmapped
 */
export function memoryPathnameToTabSlug(section, pathname) {
  const cfg = SETTINGS_MEMORY_ROUTE_TAB_CONFIG[section]
  if (!cfg || !pathname) {
    return null
  }
  const hit = Object.entries(cfg.paths)
    .sort((a, b) => b[1].length - a[1].length)
    .find(([, fullPath]) => (
      pathname === fullPath || pathname.startsWith(`${fullPath}/`)
    ))
  if (hit) {
    return hit[0]
  }
  const defaultFullPath = cfg.paths[cfg.defaultTab]
  if (defaultFullPath) {
    const basePath = defaultFullPath.slice(0, defaultFullPath.lastIndexOf('/'))
    if (pathname === basePath || pathname === `${basePath}/`) {
      return cfg.defaultTab
    }
  }
  return null
}
